<template>
  <div
    ref="viewerRef"
    class="tm-auto-size-children-viewer"
    :class="{
      'tm-auto-size-children-viewer--hiding-display-none': hidingMode === 'displayNone',
      'tm-auto-size-children-viewer--hiding-opacity0': hidingMode === 'opacity0',
    }"
  >
    <slot />
  </div>
</template>

<script lang="ts" setup>
/**
 * This component can be used to hide some of the children in the slot.
 * It also observes the list of children in the slot and emits an update event.
 * */

import { onMounted, ref, watchEffect, useTemplateRef, useMutationObserver } from '@/composition/vue/compositionApi'
import { TM_AUTOSIZE_CHILDREN_VIEWER_HIDDEN_ATTR } from '@/components/shared/autoSize/types'

const { hiddenChildrenIndexes = [], hidingMode = 'displayNone' } = defineProps<{
  hidingMode?: 'displayNone' | 'opacity0'
  hiddenChildrenIndexes?: number[]
}>()

const emit = defineEmits<{
  'update-list-of-children': [HTMLElement[]]
}>()

const viewerRef = useTemplateRef('viewerRef')
const children = ref<HTMLElement[]>([])

watchEffect(() => {
  if (!viewerRef.value) {
    return
  }
  children.value.forEach((child, index) => {
    if (hiddenChildrenIndexes.includes(index)) {
      child.setAttribute(TM_AUTOSIZE_CHILDREN_VIEWER_HIDDEN_ATTR, '')
      child.setAttribute('inert', '')
      child.setAttribute('aria-hidden', 'true')
    } else {
      child.removeAttribute(TM_AUTOSIZE_CHILDREN_VIEWER_HIDDEN_ATTR)
      child.removeAttribute('inert')
      child.removeAttribute('aria-hidden')
    }
  })
})

const fetchChildren = () => {
  if (!viewerRef.value) {
    return
  }
  children.value = Array.from(viewerRef.value.children).filter((e) => e instanceof HTMLElement)
  emit('update-list-of-children', children.value)
}

useMutationObserver(viewerRef, fetchChildren, {
  childList: true,
})

onMounted(() => {
  fetchChildren()
})
</script>

<style scoped lang="scss">
.tm-auto-size-children-viewer {
  display: contents;

  // We cannot change the class and style attributes of elements bypassing vue.
  // If an element has a bound class or style, then when it is changed,
  // it will override our added classes or styles.
  // That's why we add styles to the specific attribute

  &--hiding-display-none > :deep(*[data-tm-auto-size-children-viewer-hidden]) {
    display: none !important;
  }

  &--hiding-opacity0 > :deep(*[data-tm-auto-size-children-viewer-hidden]) {
    opacity: 0 !important;
    pointer-events: none !important;
    position: absolute !important;
  }
}
</style>
